import search from '@api/search';
import { generateExperimentHeader } from '@server/methods/search/utils';
import FetchSearchResultsException from '../../../server/exceptions/FetchSearchResultsException';
import NotFoundException from '../../../server/exceptions/NotFoundException';

/** @internal */
export const VENDORS_LOADED = '@fe-marketplace/vendors/loaded';
export const VENDORS_LOADING = '@fe-marketplace/vendors/loading';

/** @internal */
export const initialState = {
	profiles: [],
	requestKey: null,
	status: 'idle',
	total: 0,
};

const reducer = (state = initialState, action) => {
	switch (action.type) {
		case VENDORS_LOADING:
			return {
				...state,
				requestKey: action.requestKey,
				status: 'loading',
			};
		case VENDORS_LOADED:
			if (
				action.requestKey &&
				state.requestKey !== null &&
				action.requestKey !== state.requestKey
			) {
				return state;
			}
			return {
				...state,
				profiles: action.payload.profiles,
				total: action.payload.total.profiles,
				...(action.requestKey && {
					requestKey: action.requestKey,
					status: 'ready',
				}),
			};
		default:
			return state;
	}
};

const checkForSearchResponseErrors = (response, options) => {
	if ('errors' in response) {
		throw new FetchSearchResultsException(response.errors[0].message, options);
	}
};

export const fetchVendors = (args) => async (dispatch, getState) => {
	const { categoryGuid, city, state, limit = 30, member, seed } = args;
	const requestKey = categoryRecommendationRequestKey(
		categoryGuid,
		city,
		state,
	);
	dispatch({ type: VENDORS_LOADING, requestKey });
	const stateTree = getState();
	const {
		category: { code },
		visitor: { id: visitorId },
		experiments,
	} = stateTree;

	const options = {
		categoryCode: code,
		categoryGuid,
		city,
		state,
		limit,
		visitorId,
		member,
		seed,
	};

	try {
		const headers = generateExperimentHeader(experiments);
		const searchResponse = await search(options, headers);
		checkForSearchResponseErrors(searchResponse, options);
		dispatch({
			type: VENDORS_LOADED,
			payload: searchResponse.data.data.search,
			requestKey,
		});
	} catch (error) {
		if (error.message === 'Invalid Location') {
			throw new NotFoundException('Not Found', options);
		}
		throw new FetchSearchResultsException(error.message, options);
	}
};

// Will only be called for server side rendering.
export const updateVendorsFromCache = (response, requestKey) => {
	if (response.errors) {
		return {
			type: `${VENDORS_LOADED}_ERROR`,
			error: response.errors[0].message || 'Unknown',
		};
	}

	if (!response?.data?.search) {
		return {
			type: `${VENDORS_LOADED}_ERROR`,
			error: 'Empty search response. No error message',
		};
	}

	return {
		type: VENDORS_LOADED,
		payload: response.data.search,
		...(requestKey && { requestKey }),
	};
};

export const categoryRecommendationRequestKey = (categoryGuid, city, state) =>
	`${categoryGuid}:${city}:${state}`;

export const getVendorProfiles = (state) => state.vendors.profiles;
export const getVendorTotal = (state) => state.vendors.total;
export const getVendorProfilesForRequest = (
	state,
	categoryGuid,
	city,
	stateCode,
) => {
	const requestKey = categoryRecommendationRequestKey(
		categoryGuid,
		city,
		stateCode,
	);
	return state.vendors.requestKey === requestKey &&
		state.vendors.status === 'ready'
		? state.vendors.profiles
		: state.vendors.requestKey == null && state.vendors.status == null
			? state.vendors.profiles
			: [];
};

export default reducer;
